// Employee Companion chat — DESIGN §5.6.3 + §10.1.4 + §3.3.4 (Slice 8).
//
// Self-contained chat surface that consumes the Slice 5–7 backend:
//   GET  /api/employee/maeve/disclosure        — gate
//   POST /api/employee/maeve/disclosure/ack    — record ack
//   POST /api/employee/maeve/turn              — one Maeve turn
//
// Unlike Manager Sub-mode B, the server holds the conversation in a per-
// employee Durable Object — the client never sends the full history. This
// component keeps a local mirror of the visible transcript purely for
// rendering; reloading the page does NOT re-fetch a transcript because
// Phase 1 §3.3.1 doesn't expose it (and content is wiped at session end
// anyway). A refresh starts a fresh visible transcript on top of whatever
// open session exists on the server.
//
// Copy rules (DESIGN §1.4 #8 + §4.7): the CLIENT CHROME here stays
// anti-anthropomorphism-safe — no first-person Maeve voice, no relationship
// implication, no "always there for you". Maeve's own first-person utterances
// come from the CGC-signed `Employee_Companion` system prompt via Vertex
// (production gated on the joint clinical sign-off — separate from the
// greeting-voice/face sign-off of 2026-07-11); this component renders whatever
// the backend returns without adding tone.
//
// Journey 03 (User Journeys v2): Maeve's face + a "Here with you" presence
// label give the surface its calm companion feel. The face is CGC-approved
// (2026-07-11); "Here with you" is a presence status, not a §10.1.3 prohibited
// phrase or a relationship promise.
//
// Amber support resources (Cruse / Mind / EAP): surfaced through Maeve's
// CGC-signed RESPONSE PROSE at amber (the Companion prompt routes them —
// see `system-prompts` seed), NOT as client-rendered chips. A structured-chip
// UI would need a server contract (which resources the turn surfaced) + CGC
// sign-off, and would risk contradicting Maeve's own words. See
// docs/DESIGN_ALIGNMENT_DECISIONS.md.
//
// Aggregate badge (DESIGN §6.4): rendered as a subtle dot on assistant
// bubbles when aggregate is amber. Green is the common case and showing a
// green dot would clutter the surface; red transitions trigger the
// `sessionEnded` handoff and the dot is redundant. Per-category classifier
// labels stay server-side metadata (§3.3.3) — never surfaced here.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DisclosureAckResponse, DisclosureStatusResponse } from '@besideu/shared/maeve';
import { api, ApiError } from '@/lib/api.ts';
import { useRecordResourceAccess } from '@/lib/resource-access.ts';
import { useScrollToEnd } from '@/lib/use-scroll-to-end.ts';
import {
  useCompanionChat,
  DISCLOSURE_QUERY_KEY,
  type VisibleTurn,
} from '@/lib/use-companion-chat.ts';
import { Button } from './ui/Button.tsx';
import { ChatBubble, ChatPendingBubble } from './chat/ChatBubble.tsx';
import { MaeveAvatar } from './brand/MaeveAvatar.tsx';

// `DISCLOSURE_QUERY_KEY` is shared so the parent `EmployeeCompanionChat`, the
// child `QaConsentToggle`, and the turn mutation's `disclosure_required`
// handler all subscribe to the SAME cache entry (TanStack Query dedupes by
// key). Optimistic `setQueryData` writes inside the toggle propagate to the
// parent's render without prop-drilling. It lives in `use-companion-chat.ts`
// alongside the session key, so the two Companion cache keys are declared in
// one place.

export function EmployeeCompanionChat(
  props: { caseId?: string; prefill?: string | undefined } = {},
) {
  const { caseId, prefill } = props;
  // The disclosure GET is the gate. Its result drives whether we render the
  // modal, the chat, or a 503/unavailable state. We keep it as a TanStack
  // Query so the ack mutation can invalidate it and refetch.
  const disclosure = useQuery({
    queryKey: DISCLOSURE_QUERY_KEY,
    queryFn: () => api.get<DisclosureStatusResponse>('/api/employee/maeve/disclosure'),
    // No retry on 503 — the operator needs to seed the disclosure prompt
    // before there's anything to retry. TanStack's default 3-retry adds
    // ~6s of perceived hang on the no-prompt path.
    retry: false,
  });

  if (disclosure.isPending) {
    return (
      <Frame>
        <p className="min-h-0 flex-1 overflow-y-auto p-6 text-muted-foreground">Loading…</p>
      </Frame>
    );
  }

  if (disclosure.isError) {
    return (
      <Frame>
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          <UnavailableState error={disclosure.error} />
        </div>
      </Frame>
    );
  }

  if (disclosure.data.needsAcknowledgement) {
    return (
      <Frame>
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          <DisclosureGate
            status={disclosure.data}
            onDisclosureGateComplete={() => {
              // Fires on BOTH ack-success and version-mismatch-error. In both
              // cases the resolution is the same — refetch the GET so the
              // parent re-evaluates `needsAcknowledgement`. On a rotation, the
              // refetch returns the new prompt text and the gate re-renders;
              // on a normal ack, it flips false and the chat surface mounts.
              void disclosure.refetch();
            }}
          />
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <ChatSurface
        caseId={caseId}
        prefill={prefill}
        qaConsentVisible={disclosure.data.qaConsentVisible}
        qaConsentLookupFailed={disclosure.data.qaConsentLookupFailed}
      />
    </Frame>
  );
}

// Shared frame so all four branches (loading, unavailable, gate, chat) sit in
// the same visual container — avoids a layout pop when the disclosure flips
// from `needsAcknowledgement: true` to `false`. Full-bleed below `lg` inside
// the bottom-nav app shell, a bounded self-scrolling box above it — mirrors
// Manager's `/manager/maeve` (`ManagerMaeveChat.tsx`'s header comment; sized
// by the wrapper `PortalMaeve` renders, matching `ManagerAskMaeve`'s). Each
// non-chat branch owns its own padding/scroll (`p-6` + `overflow-y-auto`)
// since this frame no longer applies `p-6` itself — `ChatSurface` needs its
// header and composer to reach the frame's own edges to stay pinned.
function Frame({ children }: { children: React.ReactNode }) {
  return (
    <section
      id="talk"
      aria-label="Private space to write"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-card lg:rounded-lg lg:border lg:border-border"
    >
      {children}
    </section>
  );
}

// ----------------------------------------------------------------------------
// Disclosure gate — DESIGN §4.7, §9.3 #12, §10.1.4 (Slice 7).
//
// Renders the active AI_Disclosure prompt text and an "I understand" button
// that POSTs the ack. On `version_mismatch` (409) we refetch the GET and
// re-render — the active prompt rotated between our load and our ack.
// ----------------------------------------------------------------------------

function DisclosureGate({
  status,
  onDisclosureGateComplete,
}: {
  status: DisclosureStatusResponse;
  // Called on BOTH ack-success and version-mismatch-error. Parent refetches
  // the GET in both cases — see the call site in `EmployeeCompanionChat`.
  // Named to make the dual semantics explicit.
  onDisclosureGateComplete: () => void;
}) {
  // Focus the heading on mount so screen readers land on the disclosure
  // content when this section appears. `tabIndex={-1}` makes the heading
  // programmatically focusable without inserting it into the tab order.
  // This is a minimal-invasive substitute for a true focus trap; the gate
  // is gated at both the UI level (no textarea rendered) AND the backend
  // level (turn handler 409s without a recent ack), so a sighted user can't
  // bypass it visually and an AT user gets the disclosure announced.
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const ack = useMutation({
    mutationFn: (): Promise<DisclosureAckResponse> =>
      api.post<DisclosureAckResponse>('/api/employee/maeve/disclosure/ack', {
        disclosurePromptId: status.promptVersionId,
      }),
    onSuccess: () => onDisclosureGateComplete(),
    onError: (err) => {
      if (
        err instanceof ApiError &&
        err.status === 409 &&
        err.message === 'disclosure_version_mismatch'
      ) {
        // Active prompt rotated mid-flight. Re-fetch the GET; the user will
        // see the new disclosure text and need to ack it.
        onDisclosureGateComplete();
      }
    },
  });

  return (
    // `role="dialog"` without `aria-modal` is honest: this is an important
    // gated section that takes over the chat surface, but it is not a true
    // blocking overlay (the rest of the portal is still visible above).
    // `aria-modal="true"` was previously set but would have caused screen
    // readers honouring it (NVDA, VoiceOver) to shadow out the rest of the
    // page without a real focus trap — strictly worse than the current
    // honest semantics. A true overlay with focus trap is the right
    // long-term answer but belongs in its own slice alongside the CGC
    // governance review of the disclosure UX.
    <div role="dialog" aria-labelledby="disclosure-heading" className="space-y-4">
      <header>
        <h2
          id="disclosure-heading"
          ref={headingRef}
          tabIndex={-1}
          className="text-base font-semibold outline-none"
        >
          Before you start
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Please read this and confirm to continue. We'll show this again if you return after a
          break, or if the wording changes.
        </p>
      </header>
      <div className="rounded-md border border-border bg-muted/40 p-4 text-sm whitespace-pre-wrap text-foreground">
        {status.promptText}
      </div>
      {ack.isError && !isVersionMismatch(ack.error) ? (
        <p className="text-sm text-traffic-red">
          Couldn't record your acknowledgement. Please try again.
        </p>
      ) : null}
      <div className="flex justify-end">
        <Button onClick={() => ack.mutate()} loading={ack.isPending} className="py-2 text-sm">
          I understand
        </Button>
      </div>
    </div>
  );
}

function isVersionMismatch(err: unknown): boolean {
  return (
    err instanceof ApiError && err.status === 409 && err.message === 'disclosure_version_mismatch'
  );
}

// ----------------------------------------------------------------------------
// Chat surface — DESIGN §5.6.3 (Companion turn handler) + §3.3.4 (red-flag
// handoff) + §5.6.3 #11/#12 (wrap nudge + empathetic close, both backend-
// driven so this surface stays mechanically simple).
// ----------------------------------------------------------------------------

function ChatSurface({
  caseId,
  prefill,
  qaConsentVisible,
  qaConsentLookupFailed,
}: {
  caseId: string | undefined;
  // Support Framework §15 — an opt-in opener seeded from a support moment. It
  // ONLY pre-fills the input + focuses it; the employee chooses whether to send
  // (or edit/delete it), so it never makes them feel they must engage. The text
  // still flows through the full safety-gated turn when they send.
  prefill: string | undefined;
  qaConsentVisible: boolean;
  qaConsentLookupFailed: boolean;
}) {
  // PO Spec Phase 4 §3 (2026-06-21) — Resources Accessed recorder. The
  // red-flag handoff's crisis-link clicks fire-and-forget this; the server is
  // best-effort so a 5xx won't block the user from reaching Samaritans. A
  // no-op when there's no active `caseId` (see `useRecordResourceAccess`).
  const recordResourceAccess = useRecordResourceAccess(caseId);
  const onCrisisLinkClick = (resourceRef: string) =>
    recordResourceAccess('crisis_link_click', resourceRef);

  // Transcript + red-flag lock live in the query cache, NOT component state —
  // see `use-companion-chat.ts` for why that is a safety property and not a
  // refactor. `locked` is set when a turn returns `sessionEnded: true`; the
  // assistant message from that same turn renders alongside the handoff card
  // so the user sees the empathetic close (DESIGN §5.6.3 #12) AND the
  // crisis-resource handoff together.
  const { history, locked, send, draft, setDraft, submit } = useCompanionChat(prefill);

  // Screen-reader-only live region carrying ONLY Maeve's latest reply —
  // mirrors HR's `HrMaeveChat.tsx` (`lastMaeveReplyForAt`), which is the only
  // one of the three Maeve surfaces that had this. `polite`, not `assertive`:
  // an ordinary reply isn't interruption-worthy — WCAG 2.1 AA 4.1.3. The
  // red-flag handoff below already gets its own `role="alert"`, so this only
  // ever needs to be polite.
  const lastMaeveReplyForAt = useMemo(() => {
    const lastAssistant = [...history].reverse().find((t) => t.role === 'assistant');
    return lastAssistant ? `Maeve replied: ${lastAssistant.text}` : '';
  }, [history]);

  // Auto-scroll the newest content into view after every turn AND while a
  // reply is pending — shared with `HrMaeveChat` and `ManagerMaeveChat.tsx`'s
  // `Thread` via `useScrollToEnd`. Without this, Maeve's reply lands below
  // the Send button and stays off-screen after ~2 exchanges — for a grieving
  // user on a phone, an invisible response is a UX failure.
  //
  // `skipWhenEmpty`: NOT `history.length === 0` alone — on the very first
  // turn of a session, `send.isPending` flips true before `history` gains
  // anything (history is only appended once the response lands), so that
  // guard alone would skip scrolling the pending bubble into view on exactly
  // the turn where the user has nothing else on screen to tell them Maeve is
  // working on it. `useScrollToEnd` only skips when BOTH are empty.
  const endRef = useScrollToEnd(history.length, send.isPending, {
    skipWhenEmpty: true,
    smooth: true,
  });

  // §15 — the opener itself is seeded by `useCompanionChat` (once per distinct
  // `prefill` value, so a remount can't re-inject it — see that file). What
  // stays here is the DOM half: focus invites, with the cursor at the end so
  // the employee can edit or clear it freely. Fire-once per mount via the ref,
  // and never steal focus once the draft has been touched — `draft` is
  // deliberately absent from the deps so typing doesn't re-run this.
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const focusedPrefill = useRef(false);
  useEffect(() => {
    if (!prefill || locked || focusedPrefill.current) return;
    const el = textareaRef.current;
    if (!el || el.value !== prefill) return;
    focusedPrefill.current = true;
    el.focus();
    el.setSelectionRange(prefill.length, prefill.length);
  }, [prefill, locked, draft]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Calm companion header (Journey 03) — Maeve's face + a presence label.
          The AI/safety disclaimer stays: it is a foreseeable-harm safeguard,
          not decoration. `shrink-0`: pinned above the scrolling transcript,
          mirroring `ManagerMaeveChat.tsx`'s `ChatHeader`. */}
      <header className="shrink-0 space-y-3 border-b border-border bg-card px-4 py-3">
        <div className="flex items-center gap-3">
          <MaeveAvatar variant="warm" size={40} rings={false} />
          <div className="leading-tight">
            <p className="text-sm font-semibold text-foreground">Maeve</p>
            <p className="text-xs font-medium text-primary">Here with you</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          What you write stays here. Maeve is an AI, not a therapist or a crisis service. If you're
          in danger, call 999.
        </p>
      </header>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
        {/* `|| send.isPending`: the very first message in a session has
            `history.length === 0` right up until the reply lands, so without
            this the pending bubble would never show for that first message —
            `EmptyState` would keep rendering instead (mirrors the identical
            fix in `HrMaeveChat.tsx`). */}
        {history.length === 0 && !send.isPending ? (
          <EmptyState />
        ) : (
          <ChatTranscript history={history} isPending={send.isPending} />
        )}

        <div aria-live="polite" className="sr-only">
          {lastMaeveReplyForAt}
        </div>

        {locked ? <RedFlagHandoff onCrisisLinkClick={onCrisisLinkClick} /> : null}

        {qaConsentVisible && !locked ? (
          <QaConsentToggle lookupFailed={qaConsentLookupFailed} disabled={send.isPending} />
        ) : null}

        {send.error ? <TurnError error={send.error} onDismiss={() => send.reset()} /> : null}

        <div ref={endRef} />
      </div>

      {/* Hide the input entirely once the session is locked. Disabling the
          textarea + changing its placeholder (the prior approach) left an
          ambiguous "is this still active?" surface alongside the handoff
          card. Hiding removes the ambiguity — the only path forward after
          a red flag is to reach out via the handoff links. `shrink-0`:
          pinned below the scrolling transcript, mirroring
          `ManagerMaeveChat.tsx`'s composer. */}
      {locked ? null : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="shrink-0 space-y-2 border-t border-border bg-card px-4 py-3"
        >
          <label className="block">
            <span className="sr-only">Your message</span>
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                // Cmd/Ctrl+Enter submits — matches Manager Sub-mode B. Plain
                // Enter inserts a newline (people writing about grief often
                // write multi-line messages).
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder="Talk to Maeve…"
              // text-base = 16px to avoid iOS Safari zoom on focus.
              className="block w-full resize-y rounded-md border border-border bg-background p-3 text-base disabled:opacity-60"
              rows={3}
              maxLength={4000}
              disabled={send.isPending}
            />
          </label>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="hidden text-xs text-muted-foreground sm:block">
              Press Cmd/Ctrl + Enter to send.
            </p>
            <Button
              type="submit"
              loading={send.isPending}
              disabled={draft.trim().length === 0 || send.isPending}
              className="ml-auto py-2 text-sm"
            >
              Send
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

// QA sampler redactor slice — per-session opt-in for QA-sample review.
//
// Drives the checkbox directly off the disclosure query's cached value
// via TanStack Query optimistic updates: `onMutate` writes the next
// value into `queryClient.setQueryData` (visible immediately to every
// subscriber, including the parent `ChatSurface`); `onError` restores
// the snapshotted prior value; `onSettled` invalidates the query so a
// background refetch reconciles with the server. NO local state.
//
// Lookup-failed posture: when the server's consent-state probe failed,
// the cached `qaConsentCurrent` defaults to false and `qaConsentLookupFailed`
// is true. The toggle stays interactive — POST /maeve/qa-consent is
// independent of the failed lookup — and the inline hint surfaces the
// "we couldn't check" state. When the user clicks during lookup
// failure, the optimistic `setQueryData` updates the cache; we
// deliberately SKIP `invalidateQueries` so a follow-up refetch (which
// would return another lookupFailed=true / qaConsentCurrent=false) can't
// overwrite the user's authoritative flip. Other tabs pick up the
// change via their own focus-driven refetches once the lookup recovers.
function QaConsentToggle({ lookupFailed, disabled }: { lookupFailed: boolean; disabled: boolean }) {
  const [hint, setHint] = useState<string | null>(null);
  const queryClient = useQueryClient();
  // Re-subscribe to the same disclosure query the parent uses; TanStack
  // Query dedupes by key, so the parent and this child render off the
  // same cached value. Optimistic `setQueryData` writes propagate to
  // both subscribers without any prop-drilling.
  const disclosure = useQuery({
    queryKey: DISCLOSURE_QUERY_KEY,
    queryFn: () => api.get<DisclosureStatusResponse>('/api/employee/maeve/disclosure'),
    retry: false,
  });
  const consent = disclosure.data?.qaConsentCurrent ?? false;

  const toggle = useMutation({
    mutationFn: (next: boolean): Promise<{ ok: true; consent: boolean }> =>
      api.post<{ ok: true; consent: boolean }>('/api/employee/maeve/qa-consent', {
        consent: next,
      }),
    onMutate: async (next) => {
      // Cancel any in-flight refetch so it can't clobber our
      // optimistic write between `setQueryData` and the mutation
      // landing. Snapshot the prior cache value so onError can revert.
      await queryClient.cancelQueries({ queryKey: DISCLOSURE_QUERY_KEY });
      const prev = queryClient.getQueryData<DisclosureStatusResponse>(DISCLOSURE_QUERY_KEY);
      if (prev) {
        queryClient.setQueryData<DisclosureStatusResponse>(DISCLOSURE_QUERY_KEY, {
          ...prev,
          qaConsentCurrent: next,
          // Clear the lookup-failed banner on a successful local flip:
          // the user's POST will write the consent row authoritatively;
          // the prior "we couldn't read" indicator becomes irrelevant.
          // Round-5 finding: round-4 kept the banner visible after a
          // successful mutation, contradicting the user's just-made
          // choice. The server's next disclosure GET (post-recovery)
          // can re-surface lookupFailed if needed.
          qaConsentLookupFailed: false,
        });
      }
      setHint(null);
      return { prev };
    },
    onError: (err, _next, ctx) => {
      // Restore the snapshotted cache value so the checkbox visibly
      // reverts. `ctx?.prev` is undefined only if onMutate didn't run
      // (e.g. mutation cancelled pre-fire) — in that case the cache
      // is already untouched.
      if (ctx?.prev) {
        queryClient.setQueryData(DISCLOSURE_QUERY_KEY, ctx.prev);
      }
      if (err instanceof ApiError && err.status === 409 && err.message === 'no_open_session') {
        setHint('Start a conversation first — then you can share a redacted copy.');
        return;
      }
      setHint("We couldn't save that just now. Try again in a moment.");
    },
    onSettled: (_data, _err, _next, ctx) => {
      // Skip the refetch on the lookup-failed path: the server's
      // disclosure GET will return another `qaConsentLookupFailed:true`
      // payload with `qaConsentCurrent:false`, which would overwrite
      // the user's just-mutated value (their POST /maeve/qa-consent
      // succeeded — the consent row IS recorded server-side; only the
      // disclosure GET's lookup couldn't read it back). The cache
      // stays at the optimistic / mutation-result value until the
      // server lookup recovers and a subsequent refetch (e.g. window
      // focus) brings ground truth. Other tabs see the change via
      // their own normal refetch path once the DO/D1 lookup is
      // healthy.
      //
      // Round-5 finding: if `ctx?.prev` is undefined (cancelled
      // mutation, GC'd cache, race), the previous `ctx?.prev?.qaConsentLookupFailed`
      // expression resolved to undefined → falsy → fell through to
      // invalidate. In a lookup-failed environment that's the wrong
      // default. Err toward NOT invalidating when context is missing:
      // a follow-up window-focus refetch will bring ground truth
      // anyway, but in the meantime we don't risk overwriting an
      // authoritative flip with a stale lookup-failed payload.
      if (ctx?.prev === undefined || ctx.prev.qaConsentLookupFailed) return;
      void queryClient.invalidateQueries({ queryKey: DISCLOSURE_QUERY_KEY });
    },
  });

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    // Drop the event if a previous toggle is still in-flight. The
    // `disabled` attribute already blocks click events at the DOM
    // level; this is defence-in-depth against synthetic events.
    if (toggle.isPending) return;
    toggle.mutate(e.target.checked);
  }

  return (
    <div className="rounded-md border border-border bg-muted/30 p-3">
      {lookupFailed ? (
        <p className="mb-2 text-xs text-traffic-amber-text" role="status">
          We couldn't check your current sharing preference. Tick the box below to share a redacted
          copy from this point on, or leave it untouched to revoke any previous opt-in. Your last
          successful choice still applies until you change it here.
        </p>
      ) : null}
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={consent}
          onChange={onChange}
          disabled={disabled || toggle.isPending}
          className="mt-0.5 h-4 w-4"
          aria-describedby="qa-consent-help"
        />
        <span>
          <span className="font-medium">Share a redacted copy for quality review.</span>
          <span id="qa-consent-help" className="block text-xs text-muted-foreground">
            Optional. If you tick this, a copy of this conversation with names, contact details, and
            other identifiers removed is kept for up to 90 days for our clinical advisers to review.
            You can untick at any time.
          </span>
        </span>
      </label>
      {hint ? (
        <p className="mt-2 text-xs text-traffic-amber-text" role="status">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function EmptyState() {
  return (
    <p className="rounded-md border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground">
      Take your time — there&rsquo;s no rush, and nothing you need to say. When you&rsquo;re ready,
      type below. Anything you write goes only to Maeve, except in a safety emergency, when your
      organisation&rsquo;s HR contact is told that you may need help (your messages are not shared).
    </p>
  );
}

function ChatTranscript({ history, isPending }: { history: VisibleTurn[]; isPending: boolean }) {
  return (
    <ol className="space-y-3" aria-label="Conversation with Maeve">
      {history.map((turn, idx) => (
        // History is append-only within this component lifetime, so index is
        // a stable key. (Component unmount remounts the list, which is
        // fine.) Auto-scroll is driven by the parent's `endRef` anchor below
        // the whole scrollable region now, not a ref on the last item — see
        // `ChatSurface`.
        <li key={idx}>
          <ChatBubble
            role={turn.role}
            variant="employee"
            badge={turn.role !== 'user' && turn.aggregate === 'amber' ? <AmberBadge /> : undefined}
          >
            {turn.text}
          </ChatBubble>
        </li>
      ))}
      {/* Inside the SAME `<ol>` as the settled turns, not a sibling outside
          it — mirrors `HrMaeveChat.tsx`'s `ChatTranscript`. A screen-reader
          user stepping through "Conversation with Maeve" by list item should
          get the same experience on both surfaces this component pairs
          with. */}
      {isPending ? (
        <li>
          <ChatPendingBubble variant="employee" />
        </li>
      ) : null}
    </ol>
  );
}

// Subtle amber indicator on assistant turns — visual confirmation to the
// (privacy-aware) user that the conversation has been flagged for support,
// without putting a traffic light in their face. Green turns get no badge
// (noise); red turns trigger the handoff card so this badge would be
// redundant.
//
// Renders BOTH a colour dot AND a short text label so the signal is not
// colour-only — WCAG 1.4.1, and respects users with colour-vision
// deficiency for whom amber-on-light is the trickiest band. The text
// "noted" is deliberately soft: "flagged" reads alarmist in a grief
// context, and "amber" exposes the internal traffic-light language to a
// user who wasn't told about it.
function AmberBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-traffic-amber/10 px-1.5 py-0.5 text-[10px] font-medium tracking-normal text-traffic-amber-text normal-case">
      <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-full bg-traffic-amber" />
      <span className="sr-only">Maeve has noted a concern in this response. </span>
      <span aria-hidden="true">noted</span>
    </span>
  );
}

// ----------------------------------------------------------------------------
// Red-flag handoff (DESIGN §3.3.4 + §5.6.3 #12). Shown alongside the
// assistant's empathetic close on the same turn — Maeve's text appears in
// the transcript above; this card surfaces concrete next steps.
//
// Phone/text links use the platform `tel:` / `sms:` schemes so a tap on
// mobile dials/composes directly. Order: Samaritans first (24/7 freephone,
// most common path), Shout (text-first for people who can't speak aloud),
// 999 last as the explicit emergency.
// ----------------------------------------------------------------------------

function RedFlagHandoff({
  onCrisisLinkClick,
}: {
  onCrisisLinkClick: (resourceRef: string) => void;
}) {
  // Focus the first contact link on mount so keyboard / screen-reader users
  // land directly on an actionable next step rather than the disabled Send
  // button they were focused on at the moment the session locked. The Send
  // button is now gone (the form is hidden when `locked`), so without this
  // shift focus would default to <body> — which means a single Tab to start
  // navigating instead of a single Enter to call.
  const firstLinkRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    firstLinkRef.current?.focus();
  }, []);

  return (
    <section
      role="alert"
      aria-labelledby="handoff-heading"
      className="space-y-3 rounded-md border border-traffic-red/40 bg-traffic-red/5 p-4 text-sm"
    >
      <header>
        <p id="handoff-heading" className="text-base font-semibold text-traffic-red">
          Please reach out to someone right now
        </p>
        <p className="mt-1 text-muted-foreground">
          Your HR safety contact has been told that you may need support. They were not shown what
          you wrote.
        </p>
      </header>
      <ul className="space-y-2">
        <li>
          <a
            ref={firstLinkRef}
            href="tel:116123"
            onClick={() => onCrisisLinkClick('samaritans_voice')}
            className="block rounded-md border border-border bg-background p-3 hover:border-primary focus:border-primary focus:outline-2 focus:outline-offset-2 focus:outline-primary"
          >
            <span className="font-medium">Samaritans — 116 123</span>
            <span className="ml-2 text-muted-foreground">Free, 24/7, any time</span>
          </a>
        </li>
        <li>
          <a
            href="sms:85258"
            onClick={() => onCrisisLinkClick('shout_text')}
            className="block rounded-md border border-border bg-background p-3 hover:border-primary"
          >
            <span className="font-medium">Shout — text 85258</span>
            <span className="ml-2 text-muted-foreground">Free, 24/7, text only</span>
          </a>
        </li>
        <li>
          <a
            href="tel:999"
            onClick={() => onCrisisLinkClick('emergency_999')}
            className="block rounded-md border border-traffic-red/40 bg-background p-3 hover:border-traffic-red"
          >
            <span className="font-medium text-traffic-red">Emergency — 999</span>
            <span className="ml-2 text-muted-foreground">If you're in immediate danger</span>
          </a>
        </li>
      </ul>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Error rendering. The Companion-mode error matrix is documented in
// `apps/api/src/routes/employee.ts`:
//   503 maeve_unavailable        — prompt/safety-contact/disclosure-prompt unavailable
//   409 no_active_case           — employee has no open bereavement case
//   409 session_locked           — prior turn fired red-flag; session is locked
//   409 disclosure_required      — session-start disclosure check failed.
//                                   Handled in `send.onError` by invalidating
//                                   the disclosure query; the parent then
//                                   re-renders into the gate. Never reaches
//                                   the error renderer below.
//   502 maeve_upstream_error     — Vertex flake
// ----------------------------------------------------------------------------

function TurnError({ error, onDismiss }: { error: unknown; onDismiss: () => void }) {
  const { headline, detail, suppressDismiss } = explainTurnError(error);
  return (
    <div className="space-y-3 rounded-md border border-traffic-red/40 bg-traffic-red/5 p-4 text-sm">
      <div>
        <p className="font-medium text-traffic-red">{headline}</p>
        <p className="mt-1 text-muted-foreground">{detail}</p>
      </div>
      {suppressDismiss ? null : (
        <Button variant="secondary" onClick={onDismiss} className="py-1.5 text-sm">
          Dismiss
        </Button>
      )}
    </div>
  );
}

function explainTurnError(error: unknown): {
  headline: string;
  detail: string;
  // `session_locked` is terminal in this UI — dismissing it would imply the
  // user can retry, but they cannot. The handoff card carries the next step.
  suppressDismiss?: boolean;
} {
  if (error instanceof ApiError) {
    if (error.status === 503 && error.message === 'maeve_unavailable') {
      return {
        headline: 'Maeve is currently unavailable',
        detail:
          'Your organisation has not finished setting up this surface. Please contact your HR team.',
      };
    }
    if (error.status === 409 && error.message === 'no_active_case') {
      return {
        headline: 'Not available yet',
        detail:
          "This space opens once your manager logs a bereavement case on besideU. They can do that whenever you're ready.",
      };
    }
    if (error.status === 409 && error.message === 'session_locked') {
      return {
        headline: 'This conversation has ended',
        detail: "Please use one of the contacts below if you'd like to talk to someone.",
        suppressDismiss: true,
      };
    }
    // `disclosure_required` (409) intentionally has no case here — see the
    // `send.onError` handler above; the disclosure query is invalidated and
    // the parent re-renders into the gate, so this error never reaches the
    // renderer.
    if (error.status === 502 && error.message === 'maeve_upstream_error') {
      return {
        headline: "Couldn't reach Maeve",
        detail:
          "The service is temporarily unavailable. Your message wasn't sent — please try again in a moment.",
      };
    }
  }
  return {
    headline: 'Something went wrong',
    detail: 'Please try again, or refresh the page if the problem persists.',
  };
}

// The disclosure GET itself returned a non-2xx — most commonly 503 because
// no active AI_Disclosure prompt is loaded. Same operator-actionable copy
// as the Manager-mode counterpart.
function UnavailableState({ error }: { error: unknown }) {
  const isMaeveUnavailable =
    error instanceof ApiError && error.status === 503 && error.message === 'maeve_unavailable';
  return (
    <div className="space-y-2">
      <p className="text-base font-semibold">Maeve is currently unavailable</p>
      <p className="text-sm text-muted-foreground">
        {isMaeveUnavailable
          ? 'Your organisation has not finished setting up this surface. Please contact your HR team.'
          : 'Something went wrong loading this surface. Please refresh the page.'}
      </p>
    </div>
  );
}
